import { doc, getDoc, setDoc, updateDoc, collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Atualiza o agregado de notificações quando uma notificação é criada
 */
export const updateNotificationsAggregationOnCreate = async (notificationData) => {
  try {
    if (!notificationData || !notificationData.id) {
      return;
    }

    const notificationsAggregateRef = doc(db, 'agregadoNotificacoes', 'all');
    const notificationsAggregateDoc = await getDoc(notificationsAggregateRef);
    
    if (!notificationsAggregateDoc.exists()) {
      // Se o agregado não existe, buscar todas as notificações e criar o agregado
      try {
        const allNotificationsSnapshot = await getDocs(collection(db, 'notificacoes'));
        const allNotifications = allNotificationsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            criadoEm: data.criadoEm instanceof Date ? Timestamp.fromDate(data.criadoEm) : (data.criadoEm?.toDate ? data.criadoEm : (data.criadoEm || Timestamp.now())),
            lidaEm: data.lidaEm instanceof Date ? Timestamp.fromDate(data.lidaEm) : (data.lidaEm?.toDate ? data.lidaEm : null)
          };
        }).filter(n => n.criadoEm); // Filtrar notificações sem data
        
        // Verificar se a notificação atual já está na lista
        const existingNotificationIndex = allNotifications.findIndex(n => n.id === notificationData.id);
        const notificationEntry = {
          id: notificationData.id,
          ...notificationData,
          criadoEm: notificationData.criadoEm instanceof Date ? Timestamp.fromDate(notificationData.criadoEm) : (notificationData.criadoEm?.toDate ? notificationData.criadoEm : Timestamp.fromDate(new Date(notificationData.criadoEm))),
          lidaEm: notificationData.lidaEm instanceof Date ? Timestamp.fromDate(notificationData.lidaEm) : (notificationData.lidaEm?.toDate ? notificationData.lidaEm : null)
        };
        
        if (existingNotificationIndex === -1) {
          allNotifications.push(notificationEntry);
        } else {
          allNotifications[existingNotificationIndex] = notificationEntry;
        }
        
        // Ordenar por data de criação (mais recente primeiro)
        allNotifications.sort((a, b) => {
          const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
          const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
          return dateB.getTime() - dateA.getTime();
        });
        
        await setDoc(notificationsAggregateRef, {
          notificacoes: allNotifications,
          quantidadeTotal: allNotifications.length,
          ultimaAtualizacao: Timestamp.now()
        });
        // Agregado criado
        return;
      } catch (createError) {
        // Erro ao criar agregado
        throw createError;
      }
    }

    const currentData = notificationsAggregateDoc.data();
    const notifications = currentData.notificacoes || [];
    
    // Verificar se a notificação já existe
    const existingIndex = notifications.findIndex(n => n.id === notificationData.id);
    
    if (existingIndex === -1) {
      // Adicionar nova notificação
      const notificationEntry = {
        id: notificationData.id,
        ...notificationData,
        criadoEm: notificationData.criadoEm instanceof Date ? Timestamp.fromDate(notificationData.criadoEm) : notificationData.criadoEm,
        lidaEm: notificationData.lidaEm instanceof Date ? Timestamp.fromDate(notificationData.lidaEm) : (notificationData.lidaEm?.toDate ? notificationData.lidaEm : null)
      };
      
      notifications.push(notificationEntry);
      
      // Ordenar por data de criação (mais recente primeiro)
      notifications.sort((a, b) => {
        const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
        const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
        return dateB.getTime() - dateA.getTime();
      });
      
      await updateDoc(notificationsAggregateRef, {
        notificacoes: notifications,
        quantidadeTotal: notifications.length,
        ultimaAtualizacao: Timestamp.now()
      });
    } else {
      // Se já existe, atualizar (caso de sincronização)
      const notificationEntry = {
        id: notificationData.id,
        ...notificationData,
        criadoEm: notificationData.criadoEm instanceof Date ? Timestamp.fromDate(notificationData.criadoEm) : notificationData.criadoEm,
        lidaEm: notificationData.lidaEm instanceof Date ? Timestamp.fromDate(notificationData.lidaEm) : (notificationData.lidaEm?.toDate ? notificationData.lidaEm : null)
      };
      
      notifications[existingIndex] = notificationEntry;
      
      // Reordenar
      notifications.sort((a, b) => {
        const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
        const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
        return dateB.getTime() - dateA.getTime();
      });
      
      await updateDoc(notificationsAggregateRef, {
        notificacoes: notifications,
        quantidadeTotal: notifications.length,
        ultimaAtualizacao: Timestamp.now()
      });
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Atualiza o agregado de notificações quando uma notificação é atualizada
 */
export const updateNotificationsAggregationOnUpdate = async (notificationData) => {
  try {
    if (!notificationData || !notificationData.id) {
      return;
    }

    const notificationsAggregateRef = doc(db, 'agregadoNotificacoes', 'all');
    const notificationsAggregateDoc = await getDoc(notificationsAggregateRef);
    
    if (!notificationsAggregateDoc.exists()) {
      // Se o agregado não existe, criar buscando todas as notificações
      try {
        const allNotificationsSnapshot = await getDocs(collection(db, 'notificacoes'));
        const allNotifications = allNotificationsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            criadoEm: data.criadoEm instanceof Date ? Timestamp.fromDate(data.criadoEm) : (data.criadoEm?.toDate ? data.criadoEm : (data.criadoEm || Timestamp.now())),
            lidaEm: data.lidaEm instanceof Date ? Timestamp.fromDate(data.lidaEm) : (data.lidaEm?.toDate ? data.lidaEm : null)
          };
        }).filter(n => n.criadoEm);
        
        const existingNotificationIndex = allNotifications.findIndex(n => n.id === notificationData.id);
        const notificationEntry = {
          id: notificationData.id,
          ...notificationData,
          criadoEm: notificationData.criadoEm instanceof Date ? Timestamp.fromDate(notificationData.criadoEm) : (notificationData.criadoEm?.toDate ? notificationData.criadoEm : Timestamp.fromDate(new Date(notificationData.criadoEm))),
          lidaEm: notificationData.lidaEm instanceof Date ? Timestamp.fromDate(notificationData.lidaEm) : (notificationData.lidaEm?.toDate ? notificationData.lidaEm : null)
        };
        
        if (existingNotificationIndex === -1) {
          allNotifications.push(notificationEntry);
        } else {
          allNotifications[existingNotificationIndex] = notificationEntry;
        }
        
        allNotifications.sort((a, b) => {
          const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
          const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
          return dateB.getTime() - dateA.getTime();
        });
        
        await setDoc(notificationsAggregateRef, {
          notificacoes: allNotifications,
          quantidadeTotal: allNotifications.length,
          ultimaAtualizacao: Timestamp.now()
        });
        // Agregado criado
        return;
      } catch (createError) {
        // Erro ao criar agregado
        throw createError;
      }
    }

    const currentData = notificationsAggregateDoc.data();
    const notifications = currentData.notificacoes || [];
    
    const existingIndex = notifications.findIndex(n => n.id === notificationData.id);
    
    if (existingIndex !== -1) {
      // Atualizar notificação existente
      const notificationEntry = {
        id: notificationData.id,
        ...notificationData,
        criadoEm: notificationData.criadoEm instanceof Date ? Timestamp.fromDate(notificationData.criadoEm) : notificationData.criadoEm,
        lidaEm: notificationData.lidaEm instanceof Date ? Timestamp.fromDate(notificationData.lidaEm) : (notificationData.lidaEm?.toDate ? notificationData.lidaEm : null)
      };
      
      notifications[existingIndex] = notificationEntry;
      
      // Reordenar
      notifications.sort((a, b) => {
        const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
        const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
        return dateB.getTime() - dateA.getTime();
      });
      
      await updateDoc(notificationsAggregateRef, {
        notificacoes: notifications,
        quantidadeTotal: notifications.length,
        ultimaAtualizacao: Timestamp.now()
      });
    } else {
      // Se não existe no agregado, adicionar (caso de sincronização)
      const notificationEntry = {
        id: notificationData.id,
        ...notificationData,
        criadoEm: notificationData.criadoEm instanceof Date ? Timestamp.fromDate(notificationData.criadoEm) : notificationData.criadoEm,
        lidaEm: notificationData.lidaEm instanceof Date ? Timestamp.fromDate(notificationData.lidaEm) : (notificationData.lidaEm?.toDate ? notificationData.lidaEm : null)
      };
      
      notifications.push(notificationEntry);
      
      // Ordenar
      notifications.sort((a, b) => {
        const dateA = a.criadoEm?.toDate ? a.criadoEm.toDate() : new Date(a.criadoEm);
        const dateB = b.criadoEm?.toDate ? b.criadoEm.toDate() : new Date(b.criadoEm);
        return dateB.getTime() - dateA.getTime();
      });
      
      await updateDoc(notificationsAggregateRef, {
        notificacoes: notifications,
        quantidadeTotal: notifications.length,
        ultimaAtualizacao: Timestamp.now()
      });
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Atualiza o agregado de notificações quando uma notificação é deletada
 */
export const updateNotificationsAggregationOnDelete = async (notificationId) => {
  try {
    if (!notificationId) {
      return;
    }

    const notificationsAggregateRef = doc(db, 'agregadoNotificacoes', 'all');
    const notificationsAggregateDoc = await getDoc(notificationsAggregateRef);
    
    if (!notificationsAggregateDoc.exists()) {
      return; // Se o agregado não existe, não há nada para fazer
    }

    const currentData = notificationsAggregateDoc.data();
    const notifications = currentData.notificacoes || [];
    
    // Remover a notificação do array
    const filteredNotifications = notifications.filter(n => n.id !== notificationId);
    
    await updateDoc(notificationsAggregateRef, {
      notificacoes: filteredNotifications,
      quantidadeTotal: filteredNotifications.length,
      ultimaAtualizacao: Timestamp.now()
    });
  } catch (error) {
    throw error;
  }
};
